import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/slices/notificationPopupSlice';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';
import LoadingOverlay from '../LoadingOverlay';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const CookLiveOrders = ({ cookId, restaurantId: restaurantIdProp }) => {
    const dispatch = useDispatch();
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [filter, setFilter] = useState('all');
    const [showUnassigned, setShowUnassigned] = useState(true);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [checkedInCooks, setCheckedInCooks] = useState([]);
    const eventSourceRef = useRef(null);
    const refreshIntervalRef = useRef(null);

    useEffect(() => {
        if (restaurantId) {
            axios.get(API_ENDPOINTS.STAFF_CHECKED_IN(restaurantId), { params: { role: 'COOK' } })
                .then((res) => setCheckedInCooks(res.data || []))
                .catch(() => setCheckedInCooks([]));
        }
    }, [restaurantId]);

    useEffect(() => {
        if (cookId && restaurantId) {
            fetchOrders();
            setupSSE();
            if (autoRefresh) {
                refreshIntervalRef.current = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
            }
        }

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [cookId, restaurantId, filter, showUnassigned, autoRefresh]);

    const fetchOrders = async () => {
        try {
            setLoading(false); // Don't show loading on auto-refresh
            const response = await axios.get(API_ENDPOINTS.COOK_ORDERS(restaurantId), {
                params: { 
                    cookId, 
                    status: 'all',
                    includeUnassigned: showUnassigned ? 'true' : 'false'
                }
            });
            
            let filteredOrders = response.data || [];

            // Date range is enforced by the API (today only); optional client guard for same calendar day
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            const endOfToday = new Date(startOfToday);
            endOfToday.setDate(endOfToday.getDate() + 1);
            filteredOrders = filteredOrders.filter((order) => {
                const placedAt = order?.orderDate || order?.createdAt;
                if (!placedAt) return false;
                const t = new Date(placedAt).getTime();
                if (Number.isNaN(t)) return false;
                return t >= startOfToday.getTime() && t < endOfToday.getTime();
            });

            if (filter === 'assigned') {
                filteredOrders = filteredOrders.filter(order => 
                    order.assignedCookId && 
                    (typeof order.assignedCookId === 'object' 
                        ? order.assignedCookId._id === cookId 
                        : order.assignedCookId === cookId)
                );
            } else if (filter === 'unassigned') {
                filteredOrders = filteredOrders.filter(order => !order.assignedCookId);
            }
            
            // Newest orders first (by orderDate)
            filteredOrders.sort((a, b) => {
                const tb = new Date(b.orderDate || b.createdAt).getTime();
                const ta = new Date(a.orderDate || a.createdAt).getTime();
                return tb - ta;
            });
            
            setOrders(filteredOrders);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            if (error.response?.data?.requiresCheckIn) {
                showToast('You must be checked in to view orders', 'error');
            } else {
                showToast('Failed to load orders', 'error');
            }
        }
    };

    const setupSSE = () => {
        try {
            eventSourceRef.current = new EventSource(`${API_BASE_URL}/orders/notifications/${restaurantId}`);
            
            eventSourceRef.current.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'NEW_ORDER' || data.type === 'ORDER_UPDATE') {
                    if (data.type === 'NEW_ORDER') {
                        dispatch(showNotification({
                            title: 'New Order',
                            message: data.message || 'A new order has been placed. Check kitchen display.',
                        }));
                    }
                    fetchOrders();
                    
                    if (data.data && data.data.assignedCookId) {
                        const assignedCookId = typeof data.data.assignedCookId === 'object' 
                            ? data.data.assignedCookId._id || data.data.assignedCookId 
                            : data.data.assignedCookId;
                        
                        if (assignedCookId === cookId) {
                            if (data.type === 'NEW_ORDER' || (data.type === 'ORDER_UPDATE' && data.updateType === 'cook_assigned')) {
                                showToast(`New order #${data.data._id?.slice(-6) || '?'} assigned!`, 'success');
                            }
                        }
                    }
                }
            };

            eventSourceRef.current.onerror = (error) => {
                console.error('SSE connection error:', error);
                setTimeout(() => {
                    if (eventSourceRef.current) {
                        eventSourceRef.current.close();
                    }
                    setupSSE();
                }, 5000);
            };
        } catch (error) {
            console.error('Failed to setup SSE:', error);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleSelfAssign = async (orderId) => {
        setActionLoading(true);
        setLoadingMessage('Assigning order...');
        try {
            await axios.post(API_ENDPOINTS.COOK_ORDER_ASSIGN(orderId), { cookId });
            showToast('Order assigned successfully', 'success');
            await fetchOrders();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to assign order', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReassignCook = async (orderId, newCookId) => {
        if (!newCookId) return;
        setActionLoading(true);
        setLoadingMessage('Reassigning order...');
        try {
            await axios.patch(API_ENDPOINTS.ORDER_ASSIGN_COOK(orderId), { cookId: newCookId });
            showToast('Order reassigned', 'success');
            await fetchOrders();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to reassign order', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleStatusUpdate = async (orderId, newStatus) => {
        setActionLoading(true);
        setLoadingMessage('Updating status...');
        try {
            await axios.patch(API_ENDPOINTS.COOK_ORDER_UPDATE_STATUS(orderId), {
                status: newStatus,
                cookId
            });
            showToast('Status updated', 'success');
            await fetchOrders();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to update status', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCallWaiter = async (orderId) => {
        setActionLoading(true);
        setLoadingMessage('Calling waiter...');
        try {
            await axios.post(API_ENDPOINTS.COOK_ORDER_CALL_WAITER(orderId), {
                cookId,
                message: `Order #${orderId.slice(-6)} is ready for pickup`
            });
            showToast('Waiter notified', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to call waiter', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteText.trim() || !selectedOrder) return;
        
        setActionLoading(true);
        setLoadingMessage('Adding note...');
        try {
            await axios.post(API_ENDPOINTS.COOK_ORDER_ADD_NOTE(selectedOrder._id), {
                cookId,
                note: noteText.trim()
            });
            showToast('Note added', 'success');
            setShowNoteModal(false);
            setNoteText('');
            setSelectedOrder(null);
            await fetchOrders();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to add note', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PLACED': return 'bg-gray-500';
            case 'ACCEPTED': return 'bg-blue-500';
            case 'PREPARING': return 'bg-yellow-500';
            case 'READY': return 'bg-green-500';
            default: return 'bg-gray-500';
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PLACED': return { text: 'NEW', color: 'bg-gray-100 text-gray-800 border-gray-300' };
            case 'ACCEPTED': return { text: 'ACCEPTED', color: 'bg-blue-100 text-blue-800 border-blue-300' };
            case 'PREPARING': return { text: 'PREPARING', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
            case 'READY': return { text: 'READY', color: 'bg-green-100 text-green-800 border-green-300 animate-pulse' };
            default: return { text: status, color: 'bg-gray-100 text-gray-800' };
        }
    };

    const isOrderAssignedToMe = (order) => {
        if (!order.assignedCookId) return false;
        const assignedId = typeof order.assignedCookId === 'object' 
            ? order.assignedCookId._id || order.assignedCookId 
            : order.assignedCookId;
        return assignedId === cookId;
    };

    const getTimeAgo = (date) => {
        const minutes = Math.floor((new Date() - new Date(date)) / 60000);
        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ${minutes % 60}m ago`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading orders...</p>
                </div>
            </div>
        );
    }

    const assignedOrders = orders.filter(o => isOrderAssignedToMe(o));
    const unassignedOrders = orders.filter(o => !o.assignedCookId);
    const readyOrders = orders.filter(o => o.status === 'READY' && isOrderAssignedToMe(o));

    return (
        <div className="space-y-4">
            {/* Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-orange-500">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Orders</div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-800">{orders.length}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-blue-500">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1">My Orders</div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-800">{assignedOrders.length}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-yellow-500">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1">Unassigned</div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-800">{unassignedOrders.length}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-3 sm:p-4 border-l-4 border-green-500">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1">Ready</div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-800">{readyOrders.length}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-3 sm:p-4">
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <div className="flex gap-2 flex-1 min-w-[200px]">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                filter === 'all' ? 'bg-orange-500 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            All ({orders.length})
                        </button>
                        <button
                            onClick={() => setFilter('assigned')}
                            className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                filter === 'assigned' ? 'bg-orange-500 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Mine ({assignedOrders.length})
                        </button>
                        <button
                            onClick={() => setFilter('unassigned')}
                            className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                filter === 'unassigned' ? 'bg-orange-500 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Available ({unassignedOrders.length})
                        </button>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showUnassigned}
                            onChange={(e) => {
                                setShowUnassigned(e.target.checked);
                                setTimeout(fetchOrders, 100);
                            }}
                            className="w-4 h-4 rounded text-orange-500"
                        />
                        <span>Show unassigned</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            className="w-4 h-4 rounded text-orange-500"
                        />
                        <span>Auto-refresh</span>
                    </label>
                </div>
            </div>

            {/* Orders Grid - Kitchen Display Style */}
            {orders.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <div className="text-6xl mb-4">🍳</div>
                    <p className="text-xl text-gray-600 mb-2">No recent orders available</p>
                    <p className="text-sm text-gray-500">Only today&apos;s active kitchen orders appear here</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                    {orders.map((order) => {
                        const isAssigned = isOrderAssignedToMe(order);
                        const canSelfAssign = !order.assignedCookId && 
                            (order.status === 'PLACED' || order.status === 'ACCEPTED');
                        const statusBadge = getStatusBadge(order.status);
                        const isReady = order.status === 'READY';
                        
                        return (
                            <div
                                key={order._id}
                                className={`bg-white rounded-lg shadow-lg border-2 transition-all hover:shadow-xl ${
                                    isReady ? 'border-green-500 ring-2 ring-green-200' :
                                    order.status === 'PREPARING' ? 'border-yellow-500' :
                                    order.status === 'ACCEPTED' ? 'border-blue-500' :
                                    'border-gray-300'
                                } ${!isAssigned && order.assignedCookId ? 'opacity-60' : ''}`}
                            >
                                {/* Order Header */}
                                <div className={`p-3 sm:p-4 ${getStatusColor(order.status)} text-white rounded-t-lg`}>
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="text-lg sm:text-xl font-bold">Order #{order._id.slice(-6)}</div>
                                            <div className="text-xs sm:text-sm opacity-90 mt-1">
                                                {getTimeAgo(order.orderDate)}
                                            </div>
                                        </div>
                                        <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold border-2 ${statusBadge.color} bg-white`}>
                                            {statusBadge.text}
                                        </div>
                                    </div>
                                </div>

                                {/* Order Details */}
                                <div className="p-3 sm:p-4 space-y-3">
                                    {/* Table & Customer Info */}
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="bg-gray-50 p-2 rounded">
                                            <div className="text-xs text-gray-500">Table</div>
                                            <div className="font-semibold">{order.tableId?.tableNumber || order.tableNumber || 'Counter'}</div>
                                        </div>
                                        {order.assignedWaiterId && (
                                            <div className="bg-gray-50 p-2 rounded">
                                                <div className="text-xs text-gray-500">Waiter</div>
                                                <div className="font-semibold">
                                                    {typeof order.assignedWaiterId === 'object' 
                                                        ? order.assignedWaiterId.name 
                                                        : 'N/A'}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Items List */}
                                    <div>
                                        <div className="text-xs font-semibold text-gray-600 mb-2">ITEMS:</div>
                                        <div className="space-y-1">
                                            {order.items?.map((item, idx) => (
                                                <div key={idx} className="flex justify-between items-center bg-orange-50 p-2 rounded text-sm">
                                                    <span className="font-medium">{item.name}</span>
                                                    <span className="bg-orange-200 px-2 py-0.5 rounded font-bold">x{item.quantity}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Special Instructions */}
                                    {order.message && (
                                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded">
                                            <div className="text-xs font-semibold text-yellow-800 mb-1">⚠️ NOTE:</div>
                                            <div className="text-xs text-yellow-700">{order.message}</div>
                                        </div>
                                    )}

                                    {/* Total */}
                                    <div className="flex justify-between items-center pt-2 border-t">
                                        <span className="text-xs text-gray-500">Total</span>
                                        <span className="text-lg font-bold text-gray-800">₹{order.finalAmount || order.totalAmount || 0}</span>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        {canSelfAssign && (
                                            <button
                                                onClick={() => handleSelfAssign(order._id)}
                                                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
                                            >
                                                ✓ Assign to Me
                                            </button>
                                        )}
                                        
                                        {isAssigned && order.status === 'PLACED' && (
                                            <button
                                                onClick={() => handleStatusUpdate(order._id, 'ACCEPTED')}
                                                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
                                            >
                                                Accept Order
                                            </button>
                                        )}
                                        
                                        {isAssigned && order.status === 'ACCEPTED' && (
                                            <button
                                                onClick={() => handleStatusUpdate(order._id, 'PREPARING')}
                                                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
                                            >
                                                Start Cooking
                                            </button>
                                        )}
                                        
                                        {isAssigned && order.status === 'PREPARING' && (
                                            <button
                                                onClick={() => handleStatusUpdate(order._id, 'READY')}
                                                className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg animate-pulse"
                                            >
                                                ✓ Mark Ready
                                            </button>
                                        )}
                                        
                                        {isAssigned && isReady && (
                                            <button
                                                onClick={() => handleCallWaiter(order._id)}
                                                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
                                            >
                                                📞 Call Waiter
                                            </button>
                                        )}
                                        
                                        {isAssigned && (
                                            <button
                                                onClick={() => {
                                                    setSelectedOrder(order);
                                                    setShowNoteModal(true);
                                                }}
                                                className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-2.5 rounded-lg font-semibold text-sm transition-all"
                                            >
                                                📝 Note
                                            </button>
                                        )}
                                        {isAssigned && checkedInCooks.length > 1 && (
                                            <select
                                                className="text-sm border rounded px-2 py-2 bg-white font-medium"
                                                value=""
                                                onChange={(e) => {
                                                    const id = e.target.value;
                                                    if (id) handleReassignCook(order._id, id);
                                                    e.target.value = '';
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <option value="">Reassign to...</option>
                                                {checkedInCooks.filter((c) => c._id !== cookId).map((c) => (
                                                    <option key={c._id} value={c._id}>{c.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Note Modal */}
            {showNoteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
                        <h3 className="text-xl font-bold mb-4">Add Kitchen Note</h3>
                        <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="e.g., Ingredient unavailable, Spice level confirmation, Prep delay..."
                            className="w-full p-3 border-2 border-gray-300 rounded-lg mb-4 focus:border-orange-500 focus:outline-none"
                            rows="4"
                        />
                        <div className="flex gap-2">
                            <button
                                onClick={handleAddNote}
                                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2.5 rounded-lg font-semibold transition-all"
                            >
                                Add Note
                            </button>
                            <button
                                onClick={() => {
                                    setShowNoteModal(false);
                                    setNoteText('');
                                    setSelectedOrder(null);
                                }}
                                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2.5 rounded-lg font-semibold transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {actionLoading && <LoadingOverlay message={loadingMessage} />}
            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default CookLiveOrders;
